/**
 * Copyright © 2026 Delicious AI, LLC
 */

import { Box, Chip, Tab, Tabs } from '@mui/material'

import useT from '@/i18n/useT'
import type { ReviewWorkflow } from '@/graphql'

const WORKFLOW_ORDER: ReviewWorkflow[] = [
  'DETECTION',
  'CLASSIFICATION',
  'CORRECTION',
]

type ReviewWorkflowTabsProps = {
  value: ReviewWorkflow
  onChange: (workflow: ReviewWorkflow) => void
  counts?: Partial<Record<ReviewWorkflow, number>>
  disabled?: Partial<Record<ReviewWorkflow, boolean>>
}

export const getWorkflowLabel = (
  workflow: ReviewWorkflow,
  t: ReturnType<typeof useT>['t'],
) => {
  switch (workflow) {
    case 'DETECTION':
      return t('review.workflowTabs.detection', 'Detection')
    case 'CLASSIFICATION':
      return t('review.workflowTabs.classification', 'Classification')
    case 'CORRECTION':
      return t('review.workflowTabs.correction', 'Correction')
  }
}

function ReviewWorkflowTabs({
  value,
  onChange,
  counts,
  disabled,
}: ReviewWorkflowTabsProps) {
  const { t } = useT()

  return (
    <Tabs
      value={value}
      onChange={(_, nextValue: ReviewWorkflow) => onChange(nextValue)}
      textColor="primary"
      indicatorColor="primary"
      variant="scrollable"
      allowScrollButtonsMobile
      sx={{ borderBottom: 1, borderColor: 'divider' }}
    >
      {WORKFLOW_ORDER.map(workflow => {
        const count = counts?.[workflow]
        return (
          <Tab
            key={workflow}
            value={workflow}
            disabled={disabled?.[workflow] ?? false}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span>{getWorkflowLabel(workflow, t)}</span>
                {typeof count === 'number' ? (
                  <Chip size="small" label={count} variant="outlined" />
                ) : null}
              </Box>
            }
          />
        )
      })}
    </Tabs>
  )
}

export default ReviewWorkflowTabs
